import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Menu, MenuCheckboxItem, MenuGroup, MenuGroupLabel, MenuPopup, MenuTrigger } from "@/components/ui/menu";
import { NAMESPACES } from "@/i18n/constants";
import { ICON_KEYS, iconRegistry } from "@/icons";
import type { StreamVisibility } from "../spec/stream-visibility";
import { VISIBILITY_DISPLAY } from "./picker-rows";

const FilterIcon = iconRegistry[ICON_KEYS.FILTER];

/** Which levels the Available list shows. */
export type PickerFilter = Readonly<Record<StreamVisibility, boolean>>;

export const DEFAULT_PICKER_FILTER: PickerFilter = { shown: true, diagnostic: false, adminOnly: false };

export function isDefaultPickerFilter(filter: PickerFilter): boolean {
    return (Object.keys(DEFAULT_PICKER_FILTER) as StreamVisibility[]).every(
        (visibility) => filter[visibility] === DEFAULT_PICKER_FILTER[visibility],
    );
}

export function PickerFilterMenu({
    filter,
    onFilterChange,
    isAdmin,
}: {
    filter: PickerFilter;
    onFilterChange: (filter: PickerFilter) => void;
    isAdmin: boolean;
}) {
    const { t } = useTranslation(NAMESPACES.FEATURES.DATA_VIEWER, { keyPrefix: "stream-picker" });
    const label = t("filter-label");
    const levels = (Object.keys(VISIBILITY_DISPLAY) as StreamVisibility[]).filter(
        (visibility) => isAdmin || visibility !== "adminOnly",
    );

    return (
        <Menu>
            <MenuTrigger
                render={
                    <Button
                        aria-label={label}
                        className={"relative"}
                        size={"icon"}
                        tooltip={label}
                        variant={"outline"}
                    />
                }
            >
                <FilterIcon aria-hidden />
                {!isDefaultPickerFilter(filter) && (
                    <span aria-hidden className={"absolute top-1 right-1 size-1.5 rounded-full bg-primary"} />
                )}
            </MenuTrigger>
            <MenuPopup align={"end"}>
                <MenuGroup>
                    <MenuGroupLabel>{t("filter-title")}</MenuGroupLabel>
                    {levels.map((visibility) => {
                        const { Icon, filterKey } = VISIBILITY_DISPLAY[visibility];
                        return (
                            <MenuCheckboxItem
                                key={visibility}
                                checked={filter[visibility]}
                                onCheckedChange={(checked) => onFilterChange({ ...filter, [visibility]: checked })}
                            >
                                <span className={"flex items-center justify-between gap-4"}>
                                    {t(filterKey)}
                                    {Icon && <Icon aria-hidden className={"size-3.5 text-muted-foreground"} />}
                                </span>
                            </MenuCheckboxItem>
                        );
                    })}
                </MenuGroup>
            </MenuPopup>
        </Menu>
    );
}
